import React from "react";
import {
  Button,
  RECORD_FIELD_ROWS,
  RecordCard,
  RecordField,
  UNSET,
} from "../../../components/ui";
import { useSignedInUser } from "../../auth/hooks/useSignedInUser";
import ProfileAvatar from "./ProfileAvatar";
import { addressLabel, experienceLabel, fullName } from "../profileFormat";
import type { DietitianProfile } from "../types/settings.types";

/**
 * The same field, rows and card the brand is read back with — see
 * `RecordFields`. `Field` and `Card` stay as local names because this file
 * reads as a page of them.
 */
const Field = RecordField;
const FIELD_ROWS = RECORD_FIELD_ROWS;
const Card = RecordCard;

/**
 * A number with its dialling code. The code stays in the label's grey so the
 * value still reads as just the number — and it is rendered rather than stored,
 * the same way the form draws it beside the field.
 */
const DiallingCode: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <>
    <span className="font-normal text-ink-muted">+91</span> {children}
  </>
);

interface ProfileOverviewProps {
  profile: DietitianProfile;
  /** Opens the form, filled with these same answers. */
  onEdit: () => void;
}

/**
 * The General tab at rest: who the dietitian is, how to reach them, and what
 * they work on — the three cards the Settings screen opens with.
 *
 * Read-only, and the one control on it is the way into the form. Which is the
 * point of the pair: the tab opens on what is saved rather than on a screenful
 * of inputs, so the commonest visit — coming to check a number — needs nothing
 * to be edited, and nothing can be changed by a stray keystroke on the way past.
 *
 * On a phone the same three cards are read the way a phone reads a profile: a
 * large picture and the name over a full-width Edit, then captioned groups of
 * label-and-answer rows. From `md` up they are the design's own cards, with the
 * pairs spread across each one. It is one markup either way — see `Field` and
 * `Card` — because two copies of the same profile is two places for a field to
 * be forgotten, and a value drawn twice is a value a test finds twice.
 */
const ProfileOverview: React.FC<ProfileOverviewProps> = ({
  profile,
  onEdit,
}) => {
  const { fullName: accountName } = useSignedInUser();
  const experience = experienceLabel(profile);
  const address = addressLabel(profile);

  // Before the profile has a name on it, the account they signed in with is
  // still their name — the same one the rail greets them by. "Your profile" is
  // only what stands there for the moment before Clerk has loaded.
  const heading = fullName(profile) || accountName || "Your profile";

  return (
    <div className="flex flex-col gap-4">
      <Card>
        {/* The header a phone opens a profile with: the picture large and
            centred, the name under it, and the one action across the full
            width where a thumb is. From `md` up it is the design's own row —
            a small disc, the name beside it, and the action at the far end.

            Sized here rather than left to the avatar's default, since the two
            layouts want two very different discs. */}
        <div className="flex flex-col items-center gap-3 px-5 py-6 text-center md:flex-row md:flex-wrap md:px-6 md:py-5 md:text-left">
          <ProfileAvatar
            photo={profile.photo}
            name={profile.firstName}
            className="h-20 w-20 md:h-11 md:w-11"
            initialClassName="text-[28px] md:text-[16px]"
          />
          <h2 className="text-[20px] font-medium text-ink">{heading}</h2>
          <span className="inline-flex rounded-full border border-[#A6EDF5] bg-[#F8FEFF] px-2.5 py-0.5 text-[12px] font-medium text-[#00899B]">
            {profile.active ? "Active" : "In-active"}
          </span>

          {/* Full width on a phone, where it is the screen's one action and
              belongs under the thumb. From `md` up it is pushed to the end of
              the row instead, so it reads as an action on the header rather
              than another badge — the same place the client and enquiry
              screens put their own. */}
          <Button
            size="sm"
            className="w-full md:ml-auto md:w-auto"
            onClick={onEdit}
          >
            Edit Profile
          </Button>
        </div>

        {/* Spread edge to edge from `md` up, so the last field lines up with
            the card's right padding, the way the design has it. */}
        <div
          className={`${FIELD_ROWS} border-t border-line px-5 md:justify-between md:gap-x-8 md:px-6`}
        >
          <Field label="Gender" value={profile.gender || UNSET} />
          <Field label="Age" value={profile.age || UNSET} />
          <Field label="Experience" value={experience || UNSET} />
        </div>
      </Card>

      <Card title="Communication details">
        <div className={`${FIELD_ROWS} px-5 md:gap-x-10 md:px-6`}>
          <Field
            label="Phone:"
            value={
              profile.phone ? (
                <DiallingCode>{profile.phone}</DiallingCode>
              ) : (
                UNSET
              )
            }
          />
          <Field
            label="WhatsApp"
            value={
              profile.whatsapp ? (
                <DiallingCode>{profile.whatsapp}</DiallingCode>
              ) : (
                UNSET
              )
            }
          />
        </div>
        {/* The one answer that is not a row: an address runs to a line and a
            half on a phone, and squeezed into the right of a row it would set
            three or four words to a line. Its label goes above it instead —
            which is what a phone does with a long value too. */}
        <div className="border-t border-line px-5 py-3.5 md:px-6 md:py-4">
          <div className="text-[13px] 3xl:text-[14px] text-ink-muted">
            Address
          </div>
          <div className="mt-1 text-[14px] 3xl:text-[15px] font-medium text-ink">
            {address || UNSET}
          </div>
        </div>
      </Card>

      <Card title="Professional details">
        <div className="px-5 py-4 md:px-6">
          <div className="text-[13px] 3xl:text-[14px] text-ink-muted">
            Specializations
          </div>
          {profile.specializations.length > 0 ? (
            // Wrapped rather than fitted to one line: this card is the last on
            // the screen and has the room, and a specialization behind a "+2"
            // is the one thing on the page a reader came to check.
            <ul className="mt-3 flex flex-wrap gap-2">
              {profile.specializations.map((name) => (
                <li
                  key={name}
                  className="inline-flex items-center rounded-full bg-brand-soft px-3 py-1.5 text-[13px] 3xl:text-[14px] text-ink"
                >
                  {name}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-[14px] 3xl:text-[15px] text-ink-muted">
              None added yet - Edit Profile is where they go.
            </p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ProfileOverview;
